/**
 * Hover gestures for icons, keyed on which icon it is.
 *
 * Lucide stamps every glyph with its own class, so the gesture can follow the
 * picture rather than the control it happens to sit in. A gear turns wherever a
 * gear appears, whether that is a round icon button in the composer row or a row
 * in the settings navigation, and the two cannot drift apart as either grows.
 *
 * Each gesture is a keyframe that ends where it started (see panda.config.ts),
 * so the icon plays a beat under the pointer and settles, rather than holding a
 * pose for as long as the cursor rests there.
 *
 * Spread into the `base` of any component that renders icons and wants them to
 * answer the pointer. The host is responsible for its own default gesture, if
 * it wants one; these only speak for glyphs that have something specific to say.
 */
export const iconMotion = {
  "&:hover svg.lucide-settings": {
    animationName: "iconSpin",
    animationDuration: "0.85s",
    animationTimingFunction: "cubic-bezier(0.83, 0, 0.17, 1)",
  },
  "&:hover svg.lucide-bell, &:hover svg.lucide-bell-off": {
    animationName: "iconSwing",
    animationDuration: "0.7s",
    animationTimingFunction: "ease-out",
  },
  "&:hover svg.lucide-pin": {
    animationName: "iconLift",
    animationDuration: "0.4s",
    animationTimingFunction: "ease-in-out",
  },
  "&:hover svg.lucide-send": {
    animationName: "iconSend",
    animationDuration: "0.4s",
    animationTimingFunction: "ease-in-out",
  },
  "&:hover svg.lucide-download": {
    animationName: "iconDown",
    animationDuration: "0.4s",
    animationTimingFunction: "ease-in-out",
  },
  "&:hover svg.lucide-x, &:hover svg.lucide-trash-2": {
    animationName: "iconShake",
    animationDuration: "0.35s",
    animationTimingFunction: "ease-in-out",
  },

  // Needs the bang: the per-icon rules outrank a bare `svg` selector, so without
  // it the gear would still turn for someone who asked it not to.
  "@media (prefers-reduced-motion: reduce)": {
    "&:hover svg": { animationName: "none !important" },
  },
} as const;

/**
 * The gesture for glyphs with nothing specific to say: a short tilt and a
 * slight scale, suiting a pictogram that reads as an object.
 *
 * Kept separate from {@link iconMotion} so a host can take the per-icon rules
 * without committing every one of its icons to moving.
 */
export const iconMotionDefault = {
  "&:hover svg": {
    animationName: "iconNudge",
    animationDuration: "0.45s",
    animationTimingFunction: "ease-in-out",
  },
} as const;
